import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  clearChunkReloadFlag,
  isChunkLoadError,
  reloadAppOnce,
} from './reload';

export type ErrorFallbackProps = {
  error: Error;
  info?: {
    componentStack?: string;
  };
};

const ErrorFallback = ({ error, info: { componentStack } = {} }: ErrorFallbackProps) => {
  const location = useLocation();
  const [isReloading, setIsReloading] = useState(false);
  const chunkLoadError = isChunkLoadError(error);

  useEffect(() => {
    if (!chunkLoadError) {
      return;
    }

    if (reloadAppOnce()) {
      setIsReloading(true);
    }
  }, [chunkLoadError, error]);

  const handleGoToDashboard = useCallback(() => {
    window.location.assign('/dashboard');
  }, []);

  const handleTryAgain = useCallback(() => {
    if (chunkLoadError) {
      clearChunkReloadFlag();
      reloadAppOnce();
      return;
    }

    window.location.reload();
  }, [chunkLoadError]);

  const componentsNames = useMemo(() => {
    try {
      return Array.from(String(componentStack).matchAll(/^at ([A-Z][^\s]+)/gm))
        .splice(0, 5)
        .map(([, name]) => name);
    } catch {
      return [];
    }
  }, [componentStack]);

  if (isReloading) {
    return (
      <div className="error-fallback">
        <div className="error-fallback-content">
          <p className="error-fallback-title">Updating app…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="error-fallback">
      <div className="error-fallback-content">
        <p className="error-fallback-title">
          Oops! Something went wrong.
          <br />
          {chunkLoadError
            ? 'A new version may be available.'
            : 'New Code might be deployed'}
        </p>

        <p>Don&prime;t worry, an error occurred:</p>

        <pre className="error-fallback-message">
          {error?.message || String(error)}
        </pre>

        <pre className="error-fallback-info">
          Path: {location?.pathname}
          <br />
          Components:{' '}
          {componentsNames.length > 0
            ? `${componentsNames.join(' → ')}…`
            : 'NA'}
        </pre>

        <div className="error-fallback-actions">
          <button type="button" onClick={handleTryAgain}>
            {chunkLoadError ? 'Reload app' : 'Try again'}
          </button>

          <button type="button" onClick={handleGoToDashboard}>
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(ErrorFallback);
